
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AppLayout, AdminLayout, PortalLayout } from './components/layouts';
import { PortalLogin, PortalDashboard, ChangePassword, PortalApplicationView } from './pages/Portal';
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import UserGuide from './pages/UserGuide';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import { ApplicationForm, RegisterCommunity } from './components/Forms';
import Applications from './pages/Admin/Applications';
import { WelcomeScreen, PortalSettings, RegisteredUsers } from './pages/Admin/index';
import Communities from './pages/Admin/Communities';
import Invoices from './pages/Admin/Invoices';
import PrivateRoute from './routes/PrivateRoute';
import { ToastProvider } from './contexts/ToastContext';
import ToastNotification from './utils/ToastNotification'
import SoftLoginModal from "./components/Modals/SoftLoginModal";
import { LoadingProvider } from "./contexts/LoadingContext";
import GlobalSpinner from "./utils/GlobalSpinner";

const changeFavicon = (faviconPath) => {
  const link = document.querySelector("link[rel~='icon']");
  if (link) {
    link.href = faviconPath;
  } else {
    const newLink = document.createElement("link");
    newLink.rel = "icon";
    newLink.href = faviconPath;
    document.head.appendChild(newLink);
  }
};


const RouteTitleAndFavicon = () => {
  const location = useLocation();

  useEffect(() => {
    const routeSettings = {
      "/login": { favicon: "/favicon.ico", title: "Pingree Grove" },
      "/admin": { favicon: "/favicon.ico", title: "Pingree Grove" },
      "/": { favicon: "/favicon.ico", title: "Pingree Grove" },
    };

    const defaultSettings = { favicon: "/favicon.ico", title: "Pingree Grove" };

    // Get the appropriate settings based on the route
    const settings = Object.keys(routeSettings).find((route) => location.pathname.startsWith(route))
      ? routeSettings[Object.keys(routeSettings).find((route) => location.pathname.startsWith(route))]
      : defaultSettings;

    document.title = settings.title;
    changeFavicon(settings.favicon);
  }, [location]);

  return null;
};

function App() {
  const [showSoftLogin, setShowSoftLogin] = useState(false);
  const basename = window.location.pathname.startsWith('/stage') ? '/stage' : '';

  return (
    <Router basename={basename}>
      <LoadingProvider>
        <ToastProvider>
          <AuthProvider>
            <ToastNotification />
            <RouteTitleAndFavicon />
            {showSoftLogin && (
              <SoftLoginModal onClose={() => setShowSoftLogin(false)} />
            )}
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/application" element={<ApplicationForm />} />
                <Route path="/register" element={<RegisterCommunity />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/user-guide" element={<UserGuide />} />
              </Route>
              <Route path="/login" element={<Login />} />

              {/* Applicant Portal Routes */}
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/reset-password" element={<ResetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route element={<PrivateRoute requiredRole="applicant" />}>
                <Route path="/portal" element={<PortalLayout />}>
                  <Route path="dashboard" element={<PortalDashboard />} />
                  <Route path="application/:id" element={<PortalApplicationView />} />
                  <Route path="change-password" element={<ChangePassword />} />
                </Route>
              </Route>

              <Route element={<PrivateRoute requiredRole="admin" />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<WelcomeScreen />} />
                  <Route path="applications" element={<Applications />} />
                  <Route path="applications/:id" element={<Applications />} />
                  <Route path="communities" element={<Communities />} />
                  <Route path="invoices" element={<Invoices />} />
                  <Route path="portal-settings" element={<PortalSettings />} />
                  <Route path="users" element={<RegisteredUsers />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRole="user" />}>
                <Route path="/community/applications" element={<Applications />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </ToastProvider>
        <GlobalSpinner />
      </LoadingProvider>
    </Router>
  );
}

export default App;
